// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes — Transformation/Growth theme
export const SCENE_COLORS = {
  scene1: { primaryColor: '#ffffff', accentColor: '#FCD34D' },   // White/Gold - Struggling
  scene2: { primaryColor: '#ffffff', accentColor: '#EF4444' },   // White/Red - Empty
  scene3: { primaryColor: '#22C55E', accentColor: '#FCD34D' },   // Green/Gold - Transformation
  scene4: { primaryColor: '#3B82F6', accentColor: '#ffffff' },   // Blue/White - Busy
  scene5: { primaryColor: '#8B5CF6', accentColor: '#ffffff' },   // Purple/White - Comparison
  scene6: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - CTA
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },
};

export const SCENE_IMAGES = {
  scene1: 'images/gdm-3-to-18-jobs/scene1-struggling.png',
  scene2: 'images/gdm-3-to-18-jobs/scene2-empty.png',
  scene3: 'images/gdm-3-to-18-jobs/scene3-transformation.png',
  scene4: 'images/gdm-3-to-18-jobs/scene4-busy.png',
  scene5: 'images/gdm-3-to-18-jobs/scene5-comparison.png',
  scene6: 'images/gdm-3-to-18-jobs/scene6-report.png',
};

export const REPORT_SLIDES = [
  'images/gdm-quiet-phone/report-slide-01.png',
  'images/gdm-quiet-phone/report-slide-05.png',
  'images/gdm-quiet-phone/report-slide-07.png',
  'images/gdm-quiet-phone/report-slide-12.png',
];

export const DRAMATIC_WORDS = ['Three', 'Eighteen', 'Turning', 'Different', 'Free'];

// Word timings from Whisper — manual corrections: "18" → "Eighteen", "10" → "Ten"
export const WORD_TIMINGS: WordTiming[] = [
  // Scene 1: Start
  { text: 'Three', startMs: 0, endMs: 160 },
  { text: 'jobs', startMs: 160, endMs: 380 },
  { text: 'a', startMs: 380, endMs: 620 },
  { text: 'month.', startMs: 620, endMs: 960 },
  { text: "That's", startMs: 960, endMs: 1480 },
  { text: 'where', startMs: 1480, endMs: 1600 },
  { text: 'he', startMs: 1600, endMs: 1700 },
  { text: 'started.', startMs: 1700, endMs: 2340 },

  // Scene 2: Empty
  { text: 'Slow', startMs: 2340, endMs: 3340 },
  { text: 'phone.', startMs: 3340, endMs: 3840 },
  { text: 'Empty', startMs: 3840, endMs: 4160 },
  { text: 'calendar.', startMs: 4160, endMs: 4780 },
  { text: 'Wondering', startMs: 4780, endMs: 5540 },
  { text: 'if', startMs: 5540, endMs: 5680 },
  { text: 'it', startMs: 5680, endMs: 5760 },
  { text: 'was', startMs: 5760, endMs: 5880 },
  { text: 'worth', startMs: 5880, endMs: 6080 },
  { text: 'it.', startMs: 6080, endMs: 6600 },

  // Scene 3: Transformation
  { text: 'Six', startMs: 6600, endMs: 7520 },
  { text: 'months', startMs: 7520, endMs: 7800 },
  { text: 'later?', startMs: 7800, endMs: 8260 },
  { text: 'Eighteen', startMs: 8260, endMs: 8680 },
  { text: 'jobs', startMs: 8680, endMs: 9100 },
  { text: 'a', startMs: 9100, endMs: 9320 },
  { text: 'month.', startMs: 9320, endMs: 9880 },

  // Scene 4: Busy
  { text: 'Six', startMs: 9880, endMs: 10380 },
  { text: 'trucks', startMs: 10380, endMs: 10680 },
  { text: 'running.', startMs: 10680, endMs: 11200 },
  { text: 'Turning', startMs: 11200, endMs: 11900 },
  { text: 'down', startMs: 11900, endMs: 12120 },
  { text: 'work.', startMs: 12120, endMs: 12860 },

  // Scene 5: Comparison
  { text: 'Same', startMs: 12860, endMs: 14080 },
  { text: 'market.', startMs: 14080, endMs: 14600 },
  { text: 'Same', startMs: 14600, endMs: 15340 },
  { text: 'services.', startMs: 15340, endMs: 16140 },
  { text: 'Different', startMs: 16140, endMs: 16900 },
  { text: 'marketing.', startMs: 16900, endMs: 17700 },

  // Scene 6: CTA
  { text: "We'll", startMs: 17700, endMs: 19080 },
  { text: 'show', startMs: 19080, endMs: 19240 },
  { text: 'you', startMs: 19240, endMs: 19360 },
  { text: 'what', startMs: 19360, endMs: 19480 },
  { text: 'changed.', startMs: 19480, endMs: 20020 },
  { text: 'Free', startMs: 20020, endMs: 20560 },
  { text: 'Market', startMs: 20560, endMs: 20800 },
  { text: 'Report.', startMs: 20800, endMs: 21480 },
  { text: 'Ten', startMs: 21480, endMs: 22060 },
  { text: 'minutes.', startMs: 22060, endMs: 22560 },
  { text: 'Link', startMs: 22560, endMs: 23240 },
  { text: 'below.', startMs: 23240, endMs: 23760 },
];

export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Start',
    startMs: 0,
    endMs: 2340,
    words: WORD_TIMINGS.slice(0, 8),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Empty',
    startMs: 2340,
    endMs: 6600,
    words: WORD_TIMINGS.slice(8, 18),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Transformation',
    startMs: 6600,
    endMs: 9880,
    words: WORD_TIMINGS.slice(18, 25),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 4,
    name: 'Busy',
    startMs: 9880,
    endMs: 12860,
    words: WORD_TIMINGS.slice(25, 31),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 5,
    name: 'Comparison',
    startMs: 12860,
    endMs: 17700,
    words: WORD_TIMINGS.slice(31, 37),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 6,
    name: 'CTA',
    startMs: 17700,
    endMs: 23760,
    words: WORD_TIMINGS.slice(37),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

export const LOGO_SCENE = {
  startMs: 23760,
  endMs: 28860,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
